import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  GetApp as DownloadIcon,
  Description as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as DocIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { resourcesAPI } from '../../services/api';

const Resources = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await resourcesAPI.listResources();
      if (response.success && response.data) {
        setResources(response.data);
      } else {
        setError('Failed to load resources');
      }
    } catch (err) {
      console.error('Failed to load resources:', err);
      setError(err.message || 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (contentType) => {
    if (contentType.includes('pdf')) {
      return <PdfIcon sx={{ color: '#dc2626', fontSize: 32 }} />;
    }
    if (contentType.includes('word') || contentType.includes('document')) {
      return <DocIcon sx={{ color: '#2563eb', fontSize: 32 }} />;
    }
    if (contentType.includes('image')) {
      return <ImageIcon sx={{ color: '#10b981', fontSize: 32 }} />;
    }
    return <FileIcon sx={{ color: '#6b7280', fontSize: 32 }} />;
  };

  const handleDownload = (url, name) => {
    window.open(url, '_blank');
  };

  const handlePreview = (url, contentType) => {
    // Only preview PDFs in browser
    if (contentType.includes('pdf')) {
      window.open(url, '_blank');
    } else {
      // For other file types, just open in new tab
      window.open(url, '_blank');
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        mb: 3,
        mt: { xs: '50px', md: 0 }, // push down on mobile to sit below hamburger
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        p: 3,
        borderRadius: 2,
        color: 'white',
        pl: { xs: '16px', md: 3 },
        transition: 'margin 0.2s ease'
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
          Resources
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
          Access important documents, guides, and resources
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : resources.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <FileIcon sx={{ fontSize: 64, color: '#9ca3af', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No resources available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Resources will appear here when they are uploaded by administrators.
            </Typography>
          </CardContent>
        </Card>
      ) : isMobile ? (
        <Stack spacing={2}>
          {resources.map((resource) => (
            <Card key={resource.name} variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {getFileIcon(resource.contentType)}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {resource.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(resource.size)} • {new Date(resource.lastModified).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {resource.contentType.includes('pdf') && (
                    <Tooltip title="Preview">
                      <IconButton
                        onClick={() => handlePreview(resource.url, resource.contentType)}
                        sx={{ color: '#039582' }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Download">
                    <IconButton
                      onClick={() => handleDownload(resource.url, resource.name)}
                      sx={{ color: '#039582' }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Card>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Resource</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Size</strong></TableCell>
                    <TableCell><strong>Last Modified</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resources.map((resource) => (
                    <TableRow key={resource.name} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {getFileIcon(resource.contentType)}
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {resource.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {resource.contentType}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatFileSize(resource.size)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(resource.lastModified).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {resource.contentType.includes('pdf') && (
                            <Tooltip title="Preview">
                              <IconButton
                                onClick={() => handlePreview(resource.url, resource.contentType)}
                                sx={{ color: '#039582' }}
                                size="small"
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Download">
                            <IconButton
                              onClick={() => handleDownload(resource.url, resource.name)}
                              sx={{ color: '#039582' }}
                              size="small"
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Resources;

